// "Nu ai permisiunea pentru asta" — one place, for every request the app makes.
//
// The rule itself lives on the servers, in middleware/domainAuth.js (mirrored
// byte-for-byte between eng-backend and redis_dev): every role may READ
// everything, and writes are scoped — sales acts on sales things, production on
// production things, admin anywhere. A refusal comes back as HTTP 403 with a
// Romanian message already written for the operator, so the wording is decided
// once, on the server, and this module only has to show it.
//
// Why the axios.create patch instead of 23 edits:
//
// The app talks to the backends through 24 different axios instances — one per
// feature — plus the default export. Attaching this by hand to each would work
// today and be wrong the first time somebody adds feature number 25: their
// buttons would fail silently, with the request refused and nothing on screen.
// Patching the factory makes the interceptor the default and the failure mode
// "it shows the modal" rather than "it does nothing". Same reasoning as apiGate
// in eng-backend/server.js, which is opt-out for the same reason.
//
// This module must be imported BEFORE any module that calls axios.create — see
// the first line of src/index.js. ES modules evaluate depth-first in import
// order, so being first there is enough.

import axios from 'axios';

const listeners = new Set();

/** Subscribe to refusals. Returns an unsubscribe function. */
export const onRoleDenied = fn => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

// Shown when a 403 arrives without a body from domainAuth — a router-level
// authorize() guard, for instance, whose message is English and aimed at a
// developer. The operator gets the same sentence either way.
const FALLBACK_ERROR = 'Nu ai permisiunea pentru aceasta actiune.';
const FALLBACK_DETAIL =
  'Poti vedea toata aplicatia, dar modificarea aceasta poate fi facuta doar de ' +
  'un coleg cu rolul potrivit.';

/**
 * @returns the refusal to display, or null if this error is not one.
 *
 * Every 403 counts, not only the ones carrying `code: "ROLE_FORBIDDEN"`. A few
 * routers still guard themselves with authorize(), and a refusal the user never
 * sees is worse than one worded for a developer.
 */
export const denialFrom = err => {
  if (err?.response?.status !== 403) return null;

  const body = err.response.data || {};
  const fromDomainAuth = body.code === 'ROLE_FORBIDDEN';

  return {
    error: fromDomainAuth && body.error ? body.error : FALLBACK_ERROR,
    detail: fromDomainAuth && body.detail ? body.detail : FALLBACK_DETAIL,
    role: body.role || null,
    required: body.required || null,
    action:
      body.action ||
      `${String(err.config?.method || '').toUpperCase()} ${err.config?.url || ''}`.trim(),
    // kept for the technical line: what the server actually said, when that
    // was not the Romanian sentence above
    serverMessage: fromDomainAuth ? null : body.error || body.msg || null,
  };
};

/** Adds the reporting interceptor to one axios instance. Idempotent per instance. */
export const attachRoleDenial = instance => {
  if (!instance || instance.__roleDenialAttached) return instance;
  instance.__roleDenialAttached = true;

  instance.interceptors.response.use(
    res => res,
    err => {
      const denial = denialFrom(err);
      if (denial) listeners.forEach(fn => fn(denial));
      // Still rejected: the caller's own error handling is untouched, this only
      // adds the modal on top of whatever the page already does.
      return Promise.reject(err);
    }
  );

  return instance;
};

attachRoleDenial(axios);

const createInstance = axios.create.bind(axios);
axios.create = config => attachRoleDenial(createInstance(config));
